import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';

type FilterValue = string | number | null | undefined;
type FilterRecord = Record<string, FilterValue>;

const NUMERIC_KEYS = new Set(['page', 'limit']);

const toSearchParams = (filters: FilterRecord): URLSearchParams => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== '' && value !== null && value !== undefined) params.set(key, String(value));
  }
  return params;
};

const fromSearchParams = (params: URLSearchParams, defaults: FilterRecord): FilterRecord => {
  const restored: FilterRecord = { ...defaults };
  // Empty values are left out of the URL, so a text filter missing from it was cleared on purpose
  // (e.g. a removed default date range) and must not fall back to its default.
  for (const [key, value] of Object.entries(defaults)) {
    if (typeof value === 'string' && !params.has(key)) restored[key] = '';
  }
  for (const [key, value] of params) {
    const numeric = Number(value);
    restored[key] = NUMERIC_KEYS.has(key) ? (Number.isFinite(numeric) && numeric > 0 ? numeric : defaults[key]) : value;
  }
  return restored;
};

// Applied list filters (including page and sort) mirrored into the URL query string. Opening a
// row's link and coming back with the browser's Back button then restores the same results
// instead of resetting the page to its defaults.
export const useUrlFilterState = <T extends object>(initial: () => T): [T, Dispatch<SetStateAction<T>>] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<T>(() => (
    searchParams.toString() ? fromSearchParams(searchParams, initial() as FilterRecord) as T : initial()
  ));

  useEffect(() => {
    const next = toSearchParams(filters as FilterRecord);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  return [filters, setFilters];
};
